// TODO move to core
import React from "react";
import {
	booleanTypeFilter,
	Button,
	CardinalLocation,
	Column,
	evaluateTypeOrFunction,
	getObjectKeys,
	getObjectValues,
	makeClassName,
	makeObjectFromArrayMap,
	mapObjectEntries,
	Row,
	Tooltip,
	TooltipButton,
	TypeFilterObject,
	TypeOrFunction,
	useLazyRef,
} from "@hex-insights/core";
import {
	anyFieldChanged,
	BooleanField,
	Form,
	FormState,
	FormType,
	StandingForm,
	useFormState,
	useFormValuesQueryStateSync,
	ValidationDisplayPolicy,
} from "@hex-insights/forms";
import { useQueryStateValuesOnMount } from "@hex-insights/router";
import { Icon } from "../Icon";
import styles from "./styles.module.css";

const sideOrder: CardinalLocation[] = ["bottom", "top", "right", "left"];

type ColumnFilterFormValues<C extends string = string> = {
	[column in C]: boolean;
};

const columnFilterQueryStateKeyPrefix = "columns.";

export function useColumnFilterFormStateWithQueryStateSync(
	columns: string[],
	defaultFormValues: TypeOrFunction<ColumnFilterFormValues> = {},
) {
	const defaultFormValuesRef = useLazyRef(() => evaluateTypeOrFunction(defaultFormValues));

	const typeFilterObject = React.useMemo(
		() => getColumnFilterFormValuesFilterObject(columns, defaultFormValuesRef.current),
		[columns, defaultFormValuesRef],
	);

	const initialFormValues = useQueryStateValuesOnMount(typeFilterObject, defaultFormValuesRef.current, {
		keyPrefix: columnFilterQueryStateKeyPrefix,
	});

	const formState = useColumnFilterFormState(columns, initialFormValues);

	useFormValuesQueryStateSync({
		formState,
		typeFilterObject,
		options: { keyPrefix: columnFilterQueryStateKeyPrefix, refreshRateMS: 0, showEmpty: true },
	});

	return formState;
}

function getColumnFilterFormValuesFilterObject(
	columns: string[],
	defaultFormValues: ColumnFilterFormValues,
): TypeFilterObject<ColumnFilterFormValues> {
	// TODO: redo as a single array value field
	return {
		...makeObjectFromArrayMap(columns, (c) => [c, booleanTypeFilter(true)]),
		...mapObjectEntries(defaultFormValues, (k, v) => [k, booleanTypeFilter(v)]),
	};
}

export function useColumnFilterFormState(
	columns: string[],
	initialFormValues: Record<string, boolean> = {},
): FormState<ColumnFilterFormValues> {
	return useFormState({
		initialFormValues: () => ({ ...makeObjectFromArrayMap(columns, (c) => [c, true]), ...initialFormValues }),
		formType: FormType.Standing,
		validationDisplayPolicy: ValidationDisplayPolicy.none,
	});
}

export type ColumnFilterFormControllerProps = {
	formState: FormState<ColumnFilterFormValues>;
	buttonClassName?: string;
};

export function ColumnFilterFormController({ formState, buttonClassName }: ColumnFilterFormControllerProps) {
	const formNames = getObjectKeys(formState.formValues);
	const anyChanged = anyFieldChanged(formState);

	const onCheckAll = React.useCallback(() => {
		const setFunctions = getObjectValues(formState.formSetFunctions);
		for (let i = 0; i < setFunctions.length; i++) {
			setFunctions[i](true);
		}
	}, [formState]);

	const onUncheckAll = React.useCallback(() => {
		formState.clearFormValues();
	}, [formState]);

	return (
		<TooltipButton sideOrder={sideOrder}>
			{() => (
				<Tooltip isInteractable className={styles["tooltip"] + " tooltip--big"}>
					<Tooltip.Header>
						<strong style={{ fontSize: "1.125rem" }}>Columns</strong>
					</Tooltip.Header>
					<Tooltip.Body>
						<StandingForm>
							<Form.Column>
								<Row justify="spaced-start">
									<Button variant="secondary" size="small" onClick={onCheckAll}>
										Check All
									</Button>
									<Button variant="secondary" size="small" onClick={onUncheckAll}>
										Uncheck All
									</Button>
								</Row>

								<Column justify="spaced-start" verticalSpacing="0.375rem">
									{formNames.map((name) => (
										<BooleanField
											key={name}
											formState={formState}
											name={name}
											noToolbar
											className={styles["column-filter-form__boolean-field"]}
										/>
									))}
								</Column>
							</Form.Column>
						</StandingForm>
					</Tooltip.Body>
				</Tooltip>
			)}
			{({ onClick }) => (
				<Button
					onClick={onClick}
					title="Customize columns"
					className={makeClassName(
						styles["column-filter-form__button"],
						anyChanged ? styles["column-filter-form__button--active"] : "",
						buttonClassName,
					)}
				>
					<Icon.SlidersHorizontal className={styles["column-filter-form__button__icon"]} />
				</Button>
			)}
		</TooltipButton>
	);
}
